import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';

import { ApiError } from '@/lib/api-client';
import { useAuth } from '@/providers/AuthProvider';

type AuthorizedQueryOptions<TQueryFnData, TError, TData> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData>,
  'queryFn' | 'queryKey'
> & {
  queryKey: QueryKey;
  queryFn: (token: string) => Promise<TQueryFnData>;
};

export function useAuthorizedQuery<TQueryFnData, TError = ApiError, TData = TQueryFnData>({
  queryKey,
  queryFn,
  enabled,
  ...rest
}: AuthorizedQueryOptions<TQueryFnData, TError, TData>) {
  const { token } = useAuth();

  return useQuery<TQueryFnData, TError, TData>({
    ...rest,
    queryKey: [...queryKey, token],
    enabled: Boolean(token) && (enabled ?? true),
    queryFn: async () => {
      if (!token) {
        throw new ApiError(401, 'Missing authentication token');
      }
      return queryFn(token);
    },
  });
}
